import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, updateUserProfile, getCurrentUserProfile } from "../api";
import "./Dashboard.css";

const PROFILE_CACHE_KEY = "alumniProfileCache";

const safeParse = (value, fallback = {}) => {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

const normalizeEmailKey = (email) => {
  if (!email) return "";
  return String(email).trim().toLowerCase();
};

const isMeaningful = (v) => {
  if (v === null || v === undefined) return false;
  if (typeof v === "string") return v.trim() !== "";
  return true;
};

const mergeMeaningful = (...sources) => {
  const result = {};
  for (const src of sources) {
    if (!src || typeof src !== "object") continue;
    for (const key of Object.keys(src)) {
      if (isMeaningful(src[key])) result[key] = src[key];
    }
  }
  return result;
};

const getCachedProfileByEmail = (email) => {
  const key = normalizeEmailKey(email);
  if (!key) return {};
  const cache = safeParse(localStorage.getItem(PROFILE_CACHE_KEY), {});
  return cache[key] || {};
};

const saveCachedProfileByEmail = (profile) => {
  const keyEmail = normalizeEmailKey(profile?.email);
  if (!keyEmail) return;
  const cache = safeParse(localStorage.getItem(PROFILE_CACHE_KEY), {});
  cache[keyEmail] = mergeMeaningful(cache[keyEmail] || {}, profile);
  localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(cache));
};

const normalizeProfile = (p) => {
  if (!p || typeof p !== "object") return {};
  const out = { ...p };
  if (out.isWorking !== undefined) {
    if (typeof out.isWorking === "string") {
      const s = out.isWorking.toLowerCase().trim();
      out.isWorking = ["true", "working", "yes", "1"].includes(s);
    } else if (typeof out.isWorking === "number") {
      out.isWorking = out.isWorking === 1;
    }
  }
  if (!out.studentId && out.alumniId) out.studentId = out.alumniId;
  if (!out.alumniId && out.studentId) out.alumniId = out.studentId;
  out.mobile = out.mobile || out.phone || out.phoneNumber || out.contactNumber || "";
  out.city = out.city || out.currentCity || out.locationCity || "";
  out.country = out.country || out.locationCountry || "";
  out.profilePhoto = out.profilePhoto || out.avatar || out.photo || "";
  return out;
};

const displayValue = (value, fallback = "Not Provided") => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === "string" && value.trim() === "") return fallback;
  return value;
};

const NAV_ITEMS = [
  { id: "overview", label: "Dashboard", icon: "📊" },
  { id: "profile", label: "Personal Info", icon: "👤" },
  { id: "education", label: "Education", icon: "🎓" },
  { id: "professional", label: "Professional", icon: "💼" },
  { id: "settings", label: "Settings", icon: "⚙️" },
  { id: "members", label: "Member List", icon: "👥" }
];

const Dashboard = ({ user }) => {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("overview");
  const [profile, setProfile] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [isEditMode, setIsEditMode] = useState(false);
  const [editedProfile, setEditedProfile] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState({ type: "", text: "" });
  const [formErrors, setFormErrors] = useState({});

  const regNo = profile?.studentId ?? profile?.alumniId ?? "";

  const isCurrentlyWorking = useMemo(() => {
    if (isEditMode) return editedProfile.isWorking === true;
    return profile.isWorking === true;
  }, [profile.isWorking, editedProfile.isWorking, isEditMode]);

  const handleLogout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("loggedInUser");
    navigate("/login");
  }, [navigate]);

  const handleNavClick = useCallback((tabId) => {
    if (tabId === "members") {
      navigate("/MemberList");
      return;
    }
    setActiveTab(tabId);
    if (isEditMode) {
      setIsEditMode(false);
      setEditedProfile({});
      setFormErrors({});
    }
    setSidebarOpen(false);
  }, [navigate, isEditMode]);

  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setEditedProfile((prev) => {
      const updated = { ...prev, [name]: type === "checkbox" ? checked : value };
      if (name === "isWorking" && !checked) {
        updated.companyName = "";
        updated.jobTitle = "";
        updated.industry = "";
        updated.workExperience = "";
        updated.workLocation = "";
        updated.workMobile = "";
      }
      return updated;
    });
    if (formErrors[name]) {
      setFormErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  }, [formErrors]);

  const handleEditClick = useCallback(() => {
    setEditedProfile({ ...profile });
    setIsEditMode(true);
    setSaveMessage({ type: "", text: "" });
    setFormErrors({});
  }, [profile]);

  const handleCancelEdit = useCallback(() => {
    setIsEditMode(false);
    setEditedProfile({});
    setSaveMessage({ type: "", text: "" });
    setFormErrors({});
  }, []);

  const handlePhotoChange = useCallback((e) => {
    var file = e.target.files[0];
    if (!file) return;
    if (file.size > 1024 * 1024) {
      setSaveMessage({ type: "error", text: "Photo must be under 1MB" });
      return;
    }
    if (!file.type.startsWith("image/")) {
      setSaveMessage({ type: "error", text: "Please select a valid image" });
      return;
    }
    var reader = new FileReader();
    reader.onload = function () {
      setEditedProfile(function (prev) {
        return Object.assign({}, prev, { profilePhoto: reader.result });
      });
    };
    reader.readAsDataURL(file);
  }, []);

  const validateForm = useCallback(() => {
    var errors = {};
    var d = editedProfile;
    if (!d.name || String(d.name).trim() === "") errors.name = "Full name is required";
    if (!d.mobile || !/^\d{10}$/.test(String(d.mobile).trim())) errors.mobile = "Valid 10-digit mobile required";
    if (!d.course || String(d.course).trim() === "") errors.course = "Course is required";
    if (!d.degree || String(d.degree).trim() === "") errors.degree = "Degree is required";
    if (!d.batchYear || String(d.batchYear).trim() === "") errors.batchYear = "Batch year is required";
    if (!d.college || String(d.college).trim() === "") errors.college = "College is required";
    if (d.isWorking) {
      if (!d.companyName || String(d.companyName).trim() === "") errors.companyName = "Company required";
      if (!d.jobTitle || String(d.jobTitle).trim() === "") errors.jobTitle = "Job title required";
      if (!d.workLocation || String(d.workLocation).trim() === "") errors.workLocation = "Work location required";
      if (d.workMobile && !/^\d{10}$/.test(String(d.workMobile).trim())) errors.workMobile = "Valid 10-digit number required";
    }
    if (d.linkedin && d.linkedin.trim() !== "" && !d.linkedin.trim().startsWith("http")) {
      errors.linkedin = "URL must start with http:// or https://";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [editedProfile]);

  const handleSaveProfile = useCallback(async () => {
    if (!validateForm()) {
      setSaveMessage({ type: "error", text: "Please fix the highlighted errors" });
      return;
    }
    setIsSaving(true);
    setSaveMessage({ type: "", text: "" });
    try {
      var payload = {
        name: (editedProfile.name || "").trim(),
        mobile: (editedProfile.mobile || "").trim(),
        gender: editedProfile.gender || "",
        dob: editedProfile.dob || "",
        city: (editedProfile.city || "").trim(),
        country: (editedProfile.country || "").trim(),
        address: (editedProfile.address || "").trim(),
        bio: (editedProfile.bio || "").trim(),
        course: (editedProfile.course || "").trim(),
        degree: (editedProfile.degree || "").trim(),
        batchYear: (editedProfile.batchYear || "").trim(),
        college: (editedProfile.college || "").trim(),
        isWorking: editedProfile.isWorking || false,
        companyName: (editedProfile.companyName || "").trim(),
        jobTitle: (editedProfile.jobTitle || "").trim(),
        industry: (editedProfile.industry || "").trim(),
        workExperience: (editedProfile.workExperience || "").trim(),
        workLocation: (editedProfile.workLocation || "").trim(),
        linkedin: (editedProfile.linkedin || "").trim(),
        workMobile: (editedProfile.workMobile || "").trim(),
        profilePhoto: editedProfile.profilePhoto || ""
      };

      var result = await updateUserProfile(payload);
      if (result.success) {
        var updated = normalizeProfile(Object.assign({}, profile, payload));
        setProfile(updated);
        localStorage.setItem("loggedInUser", JSON.stringify(updated));
        saveCachedProfileByEmail(updated);
        setSaveMessage({ type: "success", text: "Profile saved successfully!" });
        setTimeout(function () {
          setIsEditMode(false);
          setEditedProfile({});
          setSaveMessage({ type: "", text: "" });
        }, 2000);
        var refresh = await getCurrentUserProfile();
        if (refresh.success && refresh.user) {
          var serverData = normalizeProfile(refresh.user);
          setProfile(serverData);
          localStorage.setItem("loggedInUser", JSON.stringify(serverData));
          saveCachedProfileByEmail(serverData);
        }
      } else {
        setSaveMessage({ type: "error", text: result.message || "Failed to save" });
      }
    } catch (err) {
      console.error("Save error:", err);
      setSaveMessage({ type: "error", text: "An error occurred. Please try again." });
    } finally {
      setIsSaving(false);
    }
  }, [editedProfile, profile, validateForm]);

  useEffect(() => {
    var mounted = true;
    var fetchProfile = async function () {
      var token = localStorage.getItem("token");
      if (!token) { navigate("/login"); return; }
      var storedUser = safeParse(localStorage.getItem("loggedInUser"), {});
      var propUser = user || {};
      var lastEmail = localStorage.getItem("lastRegisteredEmail") || "";
      var cacheEmail = propUser.email || storedUser.email || lastEmail;
      var cached = getCachedProfileByEmail(cacheEmail);
      var base = normalizeProfile(mergeMeaningful(cached, storedUser, propUser));
      if (Object.keys(base).length > 0 && mounted) setProfile(base);
      setIsLoading(true);
      setError("");
      try {
        var res = await api.get("/api/auth/me", { headers: { Authorization: "Bearer " + token } });
        var fetched = normalizeProfile(res?.data?.user || {});
        if (!fetched || Object.keys(fetched).length === 0) throw new Error("Empty response");
        var merged = normalizeProfile(mergeMeaningful(base, fetched));
        if (!mounted) return;
        setProfile(merged);
        if (merged.email) {
          localStorage.setItem("loggedInUser", JSON.stringify(merged));
          saveCachedProfileByEmail(merged);
        }
        setIsLoading(false);
      } catch (e) {
        try {
          var approvedRes = await api.get("/api/auth/approved", { headers: { Authorization: "Bearer " + token } });
          var list = Array.isArray(approvedRes.data) ? approvedRes.data : [];
          var emailKey = normalizeEmailKey(base.email);
          var idMatch = String(base.studentId || base.alumniId || "").trim();
          var found = list.find(function (m) {
            var me = normalizeEmailKey(m.email);
            return (emailKey && me === emailKey) || (idMatch && (String(m.studentId || "").trim() === idMatch || String(m.alumniId || "").trim() === idMatch));
          });
          var fromApproved = normalizeProfile(found || {});
          if (!fromApproved || Object.keys(fromApproved).length === 0) throw new Error("Not found");
          var m2 = normalizeProfile(mergeMeaningful(base, fromApproved));
          if (!mounted) return;
          setProfile(m2);
          if (m2.email) { localStorage.setItem("loggedInUser", JSON.stringify(m2)); saveCachedProfileByEmail(m2); }
          setError(""); setIsLoading(false);
        } catch (fallbackErr) {
          if (!mounted) return;
          if (Object.keys(base).length === 0) { localStorage.removeItem("token"); localStorage.removeItem("loggedInUser"); navigate("/login"); return; }
          setProfile(base);
          setError("Could not load latest data. Showing saved profile.");
          setIsLoading(false);
        }
      }
    };
    fetchProfile();
    return function () { mounted = false; };
  }, [navigate, user]);

  var dp = isEditMode ? editedProfile : profile;

  var getStatusClass = function (status) {
    var s = (status || "").toLowerCase();
    if (s === "approved") return "status-approved";
    if (s === "rejected") return "status-rejected";
    return "status-pending";
  };

  if (isLoading && Object.keys(profile).length === 0) {
    return (
      <div className="db-loader-wrap">
        <div className="db-loader">
          <div className="db-loader-spinner"></div>
          <h2>Loading Dashboard</h2>
          <p>Fetching your profile details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="db-layout">
      <button className="db-hamburger" onClick={() => setSidebarOpen(!sidebarOpen)}>
        {sidebarOpen ? "✕" : "☰"}
      </button>

      <aside className={"db-sidebar" + (sidebarOpen ? " db-sidebar-open" : "")}>
        <div className="db-sidebar-header">
          <div className="db-sidebar-logo">CIT Alumni</div>
        </div>

        <div className="db-sidebar-profile">
          {dp.profilePhoto ? (
            <img src={dp.profilePhoto} alt="" className="db-avatar-img" onError={(e) => { e.target.style.display = "none"; }} />
          ) : (
            <div className="db-avatar-circle">{(dp.name || "A").charAt(0).toUpperCase()}</div>
          )}
          <div className="db-sidebar-name">{displayValue(dp.name, "Alumni")}</div>
          <div className="db-sidebar-email">{displayValue(dp.email, "")}</div>
          <span className={"db-status-chip " + getStatusClass(dp.status)}>
            {displayValue(dp.status, "Under Verification")}
          </span>
        </div>

        <nav className="db-nav">
          {NAV_ITEMS.map(function (item) {
            return (
              <button
                key={item.id}
                className={"db-nav-btn" + (activeTab === item.id ? " db-nav-active" : "")}
                onClick={function () { handleNavClick(item.id); }}
              >
                <span className="db-nav-icon">{item.icon}</span>
                <span className="db-nav-label">{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="db-sidebar-footer">
          <button className="db-logout-btn" onClick={handleLogout}>
            🚪 Logout
          </button>
        </div>
      </aside>

      {sidebarOpen && <div className="db-overlay" onClick={() => setSidebarOpen(false)}></div>}

      <main className="db-main">
        <div className="db-topbar">
          <div className="db-topbar-left">
            <h1 className="db-page-title">
              {NAV_ITEMS.find(function (n) { return n.id === activeTab; })?.icon || "📊"}{" "}
              {NAV_ITEMS.find(function (n) { return n.id === activeTab; })?.label || "Dashboard"}
            </h1>
          </div>
          <div className="db-topbar-right">
            {!isEditMode ? (
              <button className="db-edit-btn" onClick={handleEditClick}>
                <span>✏️</span> Edit Profile
              </button>
            ) : (
              <div className="db-edit-actions">
                <button className="db-save-btn" onClick={handleSaveProfile} disabled={isSaving}>
                  {isSaving ? "⏳ Saving..." : "💾 Save Changes"}
                </button>
                <button className="db-cancel-btn" onClick={handleCancelEdit} disabled={isSaving}>
                  ✕ Cancel
                </button>
              </div>
            )}
          </div>
        </div>

        {error && (
          <div className="db-alert db-alert-warn">
            <span>⚠️ {error}</span>
            <button className="db-alert-close" onClick={() => setError("")}>×</button>
          </div>
        )}

        {saveMessage.text && (
          <div className={"db-alert " + (saveMessage.type === "success" ? "db-alert-ok" : "db-alert-err")}>
            <span>{saveMessage.type === "success" ? "✅" : "❌"} {saveMessage.text}</span>
            <button className="db-alert-close" onClick={() => setSaveMessage({ type: "", text: "" })}>×</button>
          </div>
        )}

        <div className="db-content">
          {activeTab === "overview" && (
            <div className="db-grid-4">
              <div className="db-card db-card-profile">
                <div className="db-card-header">
                  <span className="db-card-icon">👤</span>
                  <h3>Profile Summary</h3>
                </div>
                <div className="db-card-body">
                  <div className="db-info-row"><span className="db-info-label">Name</span><span className="db-info-value">{displayValue(dp.name)}</span></div>
                  <div className="db-info-row"><span className="db-info-label">Mobile</span><span className="db-info-value">{displayValue(dp.mobile)}</span></div>
                  <div className="db-info-row"><span className="db-info-label">Email</span><span className="db-info-value db-info-email">{displayValue(dp.email)}</span></div>
                  <div className="db-info-row"><span className="db-info-label">City</span><span className="db-info-value">{displayValue(dp.city)}</span></div>
                  <div className="db-info-row"><span className="db-info-label">Gender</span><span className="db-info-value">{displayValue(dp.gender)}</span></div>
                </div>
              </div>

              <div className="db-card db-card-edu">
                <div className="db-card-header">
                  <span className="db-card-icon">🎓</span>
                  <h3>Education</h3>
                </div>
                <div className="db-card-body">
                  <div className="db-info-row"><span className="db-info-label">Course</span><span className="db-info-value">{displayValue(dp.course)}</span></div>
                  <div className="db-info-row"><span className="db-info-label">Degree</span><span className="db-info-value">{displayValue(dp.degree)}</span></div>
                  <div className="db-info-row"><span className="db-info-label">College</span><span className="db-info-value">{displayValue(dp.college)}</span></div>
                  <div className="db-info-row"><span className="db-info-label">Batch</span><span className="db-info-value">{displayValue(dp.batchYear)}</span></div>
                </div>
              </div>

              <div className="db-card db-card-career">
                <div className="db-card-header">
                  <span className="db-card-icon">💼</span>
                  <h3>Career</h3>
                </div>
                <div className="db-card-body">
                  {isCurrentlyWorking ? (
                    <>
                      <div className="db-info-row"><span className="db-info-label">Company</span><span className="db-info-value">{displayValue(dp.companyName)}</span></div>
                      <div className="db-info-row"><span className="db-info-label">Title</span><span className="db-info-value">{displayValue(dp.jobTitle)}</span></div>
                      <div className="db-info-row"><span className="db-info-label">Industry</span><span className="db-info-value">{displayValue(dp.industry)}</span></div>
                      <div className="db-info-row"><span className="db-info-label">Location</span><span className="db-info-value">{displayValue(dp.workLocation)}</span></div>
                    </>
                  ) : (
                    <div className="db-not-working">
                      <span className="db-not-working-icon">🏠</span>
                      <p>Currently Not Working</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="db-card db-card-status">
                <div className="db-card-header">
                  <span className="db-card-icon">📌</span>
                  <h3>Status</h3>
                </div>
                <div className="db-card-body">
                  <div className="db-info-row">
                    <span className="db-info-label">Status</span>
                    <span className={"db-status-badge " + getStatusClass(dp.status)}>
                      {displayValue(dp.status, "Under Verification")}
                    </span>
                  </div>
                  <div className="db-info-row"><span className="db-info-label">Reg No.</span><span className="db-info-value">{displayValue(regNo)}</span></div>
                  <div className="db-info-row"><span className="db-info-label">LinkedIn</span><span className="db-info-value db-info-email">{displayValue(dp.linkedin)}</span></div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "profile" && (
            <div className="db-form-card">
              <div className="db-form-card-header">
                <h2>Personal Information</h2>
                <p>Manage your personal details and contact info</p>
              </div>

              {isEditMode && (
                <div className="db-photo-section">
                  <div className="db-photo-wrap">
                    {editedProfile.profilePhoto ? (
                      <img src={editedProfile.profilePhoto} alt="" className="db-photo-preview" />
                    ) : (
                      <div className="db-photo-placeholder">{(editedProfile.name || "A").charAt(0).toUpperCase()}</div>
                    )}
                    <div className="db-photo-controls">
                      <label className="db-photo-upload-btn">
                        📷 Upload Photo
                        <input type="file" accept="image/*" onChange={handlePhotoChange} style={{ display: "none" }} disabled={isSaving} />
                      </label>
                      {editedProfile.profilePhoto && (
                        <button className="db-photo-remove" onClick={() => setEditedProfile((p) => ({ ...p, profilePhoto: "" }))} disabled={isSaving}>
                          🗑️ Remove
                        </button>
                      )}
                      <span className="db-photo-hint">Max 1MB, JPG/PNG</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="db-form-grid">
                <div className="db-field">
                  <label>Full Name {isEditMode && <span className="db-req">*</span>}</label>
                  {isEditMode ? (
                    <>
                      <input type="text" name="name" value={dp.name || ""} onChange={handleInputChange} className={formErrors.name ? "db-input-err" : ""} placeholder="Enter full name" />
                      {formErrors.name && <span className="db-err-msg">{formErrors.name}</span>}
                    </>
                  ) : (
                    <div className="db-field-value">{displayValue(profile.name)}</div>
                  )}
                </div>

                <div className="db-field">
                  <label>Email</label>
                  <div className="db-field-value db-field-locked">{displayValue(profile.email)}<span className="db-lock-icon">🔒</span></div>
                </div>

                <div className="db-field">
                  <label>Mobile {isEditMode && <span className="db-req">*</span>}</label>
                  {isEditMode ? (
                    <>
                      <input type="tel" name="mobile" value={dp.mobile || ""} onChange={handleInputChange} className={formErrors.mobile ? "db-input-err" : ""} placeholder="10-digit number" maxLength={10} />
                      {formErrors.mobile && <span className="db-err-msg">{formErrors.mobile}</span>}
                    </>
                  ) : (
                    <div className="db-field-value">{displayValue(profile.mobile)}</div>
                  )}
                </div>

                <div className="db-field">
                  <label>Gender</label>
                  {isEditMode ? (
                    <select name="gender" value={dp.gender || ""} onChange={handleInputChange}>
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  ) : (
                    <div className="db-field-value">{displayValue(profile.gender)}</div>
                  )}
                </div>

                <div className="db-field">
                  <label>Date of Birth</label>
                  {isEditMode ? (
                    <input type="date" name="dob" value={dp.dob || ""} onChange={handleInputChange} />
                  ) : (
                    <div className="db-field-value">{displayValue(profile.dob)}</div>
                  )}
                </div>

                <div className="db-field">
                  <label>City</label>
                  {isEditMode ? (
                    <input type="text" name="city" value={dp.city || ""} onChange={handleInputChange} placeholder="Your city" />
                  ) : (
                    <div className="db-field-value">{displayValue(profile.city)}</div>
                  )}
                </div>

                <div className="db-field">
                  <label>Country</label>
                  {isEditMode ? (
                    <input type="text" name="country" value={dp.country || ""} onChange={handleInputChange} placeholder="Your country" />
                  ) : (
                    <div className="db-field-value">{displayValue(profile.country)}</div>
                  )}
                </div>

                <div className="db-field db-field-full">
                  <label>Address</label>
                  {isEditMode ? (
                    <textarea name="address" value={dp.address || ""} onChange={handleInputChange} placeholder="Enter your address" rows={3} />
                  ) : (
                    <div className="db-field-value">{displayValue(profile.address)}</div>
                  )}
                </div>

                <div className="db-field db-field-full">
                  <label>Bio</label>
                  {isEditMode ? (
                    <textarea name="bio" value={dp.bio || ""} onChange={handleInputChange} placeholder="Tell us about yourself" rows={4} />
                  ) : (
                    <div className="db-field-value">{displayValue(profile.bio)}</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === "education" && (
            <div className="db-form-card">
              <div className="db-form-card-header">
                <h2>Education Details</h2>
                <p>Your academic background information</p>
              </div>
              <div className="db-form-grid">
                <div className="db-field">
                  <label>College / University {isEditMode && <span className="db-req">*</span>}</label>
                  {isEditMode ? (
                    <>
                      <input type="text" name="college" value={dp.college || ""} onChange={handleInputChange} className={formErrors.college ? "db-input-err" : ""} placeholder="College name" />
                      {formErrors.college && <span className="db-err-msg">{formErrors.college}</span>}
                    </>
                  ) : (
                    <div className="db-field-value">{displayValue(profile.college)}</div>
                  )}
                </div>

                <div className="db-field">
                  <label>Course / Department {isEditMode && <span className="db-req">*</span>}</label>
                  {isEditMode ? (
                    <>
                      <input type="text" name="course" value={dp.course || ""} onChange={handleInputChange} className={formErrors.course ? "db-input-err" : ""} placeholder="e.g., Computer Science" />
                      {formErrors.course && <span className="db-err-msg">{formErrors.course}</span>}
                    </>
                  ) : (
                    <div className="db-field-value">{displayValue(profile.course)}</div>
                  )}
                </div>

                <div className="db-field">
                  <label>Degree {isEditMode && <span className="db-req">*</span>}</label>
                  {isEditMode ? (
                    <>
                      <input type="text" name="degree" value={dp.degree || ""} onChange={handleInputChange} className={formErrors.degree ? "db-input-err" : ""} placeholder="e.g., B.E., B.Tech" />
                      {formErrors.degree && <span className="db-err-msg">{formErrors.degree}</span>}
                    </>
                  ) : (
                    <div className="db-field-value">{displayValue(profile.degree)}</div>
                  )}
                </div>

                <div className="db-field">
                  <label>Batch Year {isEditMode && <span className="db-req">*</span>}</label>
                  {isEditMode ? (
                    <>
                      <input type="text" name="batchYear" value={dp.batchYear || ""} onChange={handleInputChange} className={formErrors.batchYear ? "db-input-err" : ""} placeholder="e.g., 2020" />
                      {formErrors.batchYear && <span className="db-err-msg">{formErrors.batchYear}</span>}
                    </>
                  ) : (
                    <div className="db-field-value">{displayValue(profile.batchYear)}</div>
                  )}
                </div>

                <div className="db-field">
                  <label>Registration No.</label>
                  <div className="db-field-value db-field-locked">{displayValue(regNo)}<span className="db-lock-icon">🔒</span></div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "professional" && (
            <div className="db-form-card">
              <div className="db-form-card-header">
                <h2>Professional Details</h2>
                <p>Your work and career information</p>
              </div>
              <div className="db-form-grid">
                {isEditMode && (
                  <div className="db-field db-field-full">
                    <label className="db-toggle-label">
                      <input type="checkbox" name="isWorking" checked={dp.isWorking || false} onChange={handleInputChange} className="db-toggle-check" />
                      <span className="db-toggle-slider"></span>
                      <span className="db-toggle-text">I am currently working</span>
                    </label>
                  </div>
                )}

                {!isEditMode && (
                  <div className="db-field db-field-full">
                    <label>Working Status</label>
                    <div className="db-field-value">
                      <span className={"db-work-badge " + (isCurrentlyWorking ? "db-work-yes" : "db-work-no")}>
                        {isCurrentlyWorking ? "✅ Currently Working" : "🏠 Not Working"}
                      </span>
                    </div>
                  </div>
                )}

                <div className="db-field">
                  <label>Company Name {isEditMode && dp.isWorking && <span className="db-req">*</span>}</label>
                  {isEditMode ? (
                    <>
                      <input type="text" name="companyName" value={dp.companyName || ""} onChange={handleInputChange} className={formErrors.companyName ? "db-input-err" : ""} placeholder="Company name" disabled={!dp.isWorking} />
                      {formErrors.companyName && <span className="db-err-msg">{formErrors.companyName}</span>}
                    </>
                  ) : (
                    <div className="db-field-value">{displayValue(profile.companyName)}</div>
                  )}
                </div>

                <div className="db-field">
                  <label>Job Title {isEditMode && dp.isWorking && <span className="db-req">*</span>}</label>
                  {isEditMode ? (
                    <>
                      <input type="text" name="jobTitle" value={dp.jobTitle || ""} onChange={handleInputChange} className={formErrors.jobTitle ? "db-input-err" : ""} placeholder="Job title" disabled={!dp.isWorking} />
                      {formErrors.jobTitle && <span className="db-err-msg">{formErrors.jobTitle}</span>}
                    </>
                  ) : (
                    <div className="db-field-value">{displayValue(profile.jobTitle)}</div>
                  )}
                </div>

                <div className="db-field">
                  <label>Industry</label>
                  {isEditMode ? (
                    <input type="text" name="industry" value={dp.industry || ""} onChange={handleInputChange} placeholder="e.g., IT, Finance" disabled={!dp.isWorking} />
                  ) : (
                    <div className="db-field-value">{displayValue(profile.industry)}</div>
                  )}
                </div>

                <div className="db-field">
                  <label>Work Location {isEditMode && dp.isWorking && <span className="db-req">*</span>}</label>
                  {isEditMode ? (
                    <>
                      <input type="text" name="workLocation" value={dp.workLocation || ""} onChange={handleInputChange} className={formErrors.workLocation ? "db-input-err" : ""} placeholder="City, Country" disabled={!dp.isWorking} />
                      {formErrors.workLocation && <span className="db-err-msg">{formErrors.workLocation}</span>}
                    </>
                  ) : (
                    <div className="db-field-value">{displayValue(profile.workLocation)}</div>
                  )}
                </div>

                <div className="db-field">
                  <label>Experience</label>
                  {isEditMode ? (
                    <input type="text" name="workExperience" value={dp.workExperience || ""} onChange={handleInputChange} placeholder="e.g., 3 years" disabled={!dp.isWorking} />
                  ) : (
                    <div className="db-field-value">{displayValue(profile.workExperience)}</div>
                  )}
                </div>

                <div className="db-field">
                  <label>LinkedIn</label>
                  {isEditMode ? (
                    <>
                      <input type="url" name="linkedin" value={dp.linkedin || ""} onChange={handleInputChange} className={formErrors.linkedin ? "db-input-err" : ""} placeholder="https://linkedin.com/in/..." />
                      {formErrors.linkedin && <span className="db-err-msg">{formErrors.linkedin}</span>}
                    </>
                  ) : (
                    <div className="db-field-value db-info-email">{displayValue(profile.linkedin)}</div>
                  )}
                </div>

                <div className="db-field">
                  <label>Work Mobile</label>
                  {isEditMode ? (
                    <>
                      <input type="tel" name="workMobile" value={dp.workMobile || ""} onChange={handleInputChange} className={formErrors.workMobile ? "db-input-err" : ""} placeholder="10-digit number" maxLength={10} disabled={!dp.isWorking} />
                      {formErrors.workMobile && <span className="db-err-msg">{formErrors.workMobile}</span>}
                    </>
                  ) : (
                    <div className="db-field-value">{displayValue(profile.workMobile)}</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === "settings" && (
            <div className="db-form-card">
              <div className="db-form-card-header">
                <h2>Account Settings</h2>
                <p>Manage your account preferences</p>
              </div>
              <div className="db-settings-grid">
                <div className="db-settings-item">
                  <span className="db-settings-icon">👤</span>
                  <div><div className="db-settings-label">Name</div><div className="db-settings-val">{displayValue(profile.name)}</div></div>
                </div>
                <div className="db-settings-item">
                  <span className="db-settings-icon">📧</span>
                  <div><div className="db-settings-label">Email</div><div className="db-settings-val">{displayValue(profile.email)}</div></div>
                </div>
                <div className="db-settings-item">
                  <span className="db-settings-icon">📋</span>
                  <div><div className="db-settings-label">Status</div><div className="db-settings-val"><span className={"db-status-badge " + getStatusClass(profile.status)}>{displayValue(profile.status, "Under Verification")}</span></div></div>
                </div>
                <div className="db-settings-item">
                  <span className="db-settings-icon">🆔</span>
                  <div><div className="db-settings-label">Registration No.</div><div className="db-settings-val">{displayValue(regNo)}</div></div>
                </div>
              </div>
              <div className="db-settings-info">
                <div className="db-settings-info-icon">💡</div>
                <div>
                  <p>Use the <strong>Edit Profile</strong> button to update your personal, education, and professional details.</p>
                  <p>Email and Registration Number cannot be changed.</p>
                </div>
              </div>
              <div className="db-settings-danger">
                <h3>Danger Zone</h3>
                <p>Logging out will end your current session.</p>
                <button className="db-danger-btn" onClick={handleLogout}>🚪 Logout</button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;